import { AssetEdge, Maybe } from "@/shared/services/graphql/generated/admin-graph-types";
import { AssetSortFields, SortOrder } from "@/shared/services/graphql/generated/consumer-graph-types";
import { ComponentDefinition } from "@/shared/components/component-definition.model";
import { LazyLoadingDataSource } from "@/shared/components/scroll/lazy-loading-datasource";
import { computed, Ref, ref } from "vue";
import { advAsset } from "../data-objects/advanced-asset";
import AssetListCard from "./components/product-selection/asset-list-card.vue";
import { useI18n } from "vue-i18n";
import { SortEntry } from "@/shared/components/sort-dropdown/sort-dropdown.model";

/**
 * Beschreibung eines Filtertypen. Mit dem für den User anzuzeigenden Text und dem Feld das damit gefiltert werden soll
 */
export interface FilterDescription {
    fieldName: string;
    displayTextKey: string;
}

export function createDataSource(
    showJumpToProduct: boolean,
    emitSelection: boolean
): LazyLoadingDataSource<Maybe<AssetEdge>, ComponentDefinition> {
    return new LazyLoadingDataSource<Maybe<AssetEdge>, ComponentDefinition>((item) => {
        return item
            ? {
                  getComponent: () => AssetListCard,
                  props: {
                      asset: advAsset(item.node),
                      showJumpToProduct: showJumpToProduct,
                      emitSelection: emitSelection,
                  },
              }
            : undefined;
    });
}

export function createListCards(
    dataSource: LazyLoadingDataSource<Maybe<AssetEdge>, ComponentDefinition>,
    assets: Array<Maybe<AssetEdge>>
): Array<ComponentDefinition> {
    dataSource.setSourceValues(assets ?? []);
    return dataSource.getResults();
}

export function useAssetsOverviewSort(isSearch: boolean) {
    const { t } = useI18n();

    const sorting: Ref<SortEntry> = ref(
        isSearch
            ? {
                  field: AssetSortFields.score,
                  order: SortOrder.desc,
              }
            : {
                  field: AssetSortFields.title,
                  order: SortOrder.asc,
              }
    );

    const sortOrderItems = computed(() => {
        return [
            ...(isSearch
                ? [
                      {
                          id: "Ranking",
                          title: t("shared.Ranking"),
                          value: {
                              field: AssetSortFields.score,
                              order: SortOrder.desc,
                          },
                      },
                  ]
                : []),
            {
                id: "ID ascending",
                title: t("product-and-asset.ID ascending"),
                value: {
                    field: AssetSortFields.assetId,
                    order: SortOrder.asc,
                },
            },
            {
                id: "ID descending",
                title: t("product-and-asset.ID descending"),
                value: {
                    field: AssetSortFields.assetId,
                    order: SortOrder.desc,
                },
            },
            {
                id: "Title ascending",
                title: t("product-and-asset.Title ascending"),
                value: {
                    field: AssetSortFields.title,
                    order: SortOrder.asc,
                },
            },
            {
                id: "Title descending",
                title: t("product-and-asset.Title descending"),
                value: {
                    field: AssetSortFields.title,
                    order: SortOrder.desc,
                },
            },
        ];
    });

    return {
        sortOrderItems,
        sorting,
    };
}
